'use client';
import { config } from '@/configs/configPortal';
import { BRANDS_MAIN_BIKE, BRANDS_MAIN_CAR, TEXT_TYPE_VEHICLE } from '@/helpers/constants';
import { capitalize, formatToSlug, isVehicleType } from '@/helpers/utils';
import { SelectOptionsProps } from '@/interfaces/filters';
import { useGeneralStore } from '@/providers/general-store-provider';
import Image from 'next/image';
import Link from 'next/link';
import { Key, useEffect, useMemo, useState } from 'react';
import './brand.scss';

const PLURAL = true;

export function BrandList({ brandsCar, brandsBike }) {
    const currentUrl = config.urlTableFipe;
    const { vehicleType } = useGeneralStore();

    const isBike = useMemo(() => isVehicleType(vehicleType, 'bike'), [vehicleType]);

    const [listFullVisible, setListFullVisible] = useState(false);
    const [brandsList, setBrandsList] = useState(getInitialBrandsList());
    const [brandsMain, setBrandsMain] = useState(getInitialBrandsMain());

    useEffect(() => {
        if (isBike) {
            setBrandsList(brandsBike);
            setBrandsMain(BRANDS_MAIN_BIKE);
        } else {
            setBrandsList(brandsCar);
            setBrandsMain(BRANDS_MAIN_CAR);
        }
    }, [isBike]);

    const toggleList = () => {
        setListFullVisible(!listFullVisible);
    };

    const sortBrands = (list: SelectOptionsProps[]) => {
        const brandsFilter = list?.filter((brand) => brandsMain.includes(brand.value));

        const orderBrandsFilter = brandsFilter?.sort(
            (a, b) => brandsMain.indexOf(a.value) - brandsMain.indexOf(b.value),
        );

        const brandsRemaining = list?.filter((brand) => !brandsMain.includes(brand.value));
        return orderBrandsFilter?.concat(brandsRemaining);
    };

    const sortedBrands = sortBrands(brandsList.originalList);

    function getTypeVehicleText(plural = true) {
        if (plural) {
            return isBike ? TEXT_TYPE_VEHICLE.bikes : TEXT_TYPE_VEHICLE.cars;
        }
        return isBike ? TEXT_TYPE_VEHICLE.bike : TEXT_TYPE_VEHICLE.car;
    }

    function getInitialBrandsList() {
        return isBike ? brandsBike : brandsCar;
    }

    function getInitialBrandsMain() {
        return isBike ? BRANDS_MAIN_BIKE : BRANDS_MAIN_CAR;
    }

    return (
        <>
            <h2 data-testid="brand-list-title">Consulte {getTypeVehicleText()} por marca</h2>
            <div className="home-brands-container" data-testid="home-brands-container">
                <div className="grid-brands" data-testid="grid-brands">
                    {listFullVisible
                        ? sortedBrands?.map((item: SelectOptionsProps, index: Key) => {
                            return (
                                <div key={index} data-testid={`brand-item-${index}`} id={`brand-item-${index}`}>
                                    <Link
                                        href={`${currentUrl}/${getTypeVehicleText()}/${formatToSlug(item.value)}`}
                                        className="brand-logo"
                                        data-testid={`brand-link-${index}`}
                                        id={`brand-link-${index}`}
                                    >
                                        <div data-testid={`brand-image-container-${index}`} id={`brand-image-container-${index}`}>
                                            <Image
                                                src={`${process.env.NEXT_PUBLIC_API_BASE_URL}/imagens/prod/portal/img/logos/${getTypeVehicleText(!PLURAL)}/${item.value}.webp?w=70&h=70&q=70&t=true&z=3106`}
                                                alt={`LOGO ${item.label}`}
                                                title={item.label}
                                                width="70"
                                                height="70"
                                                loading='lazy'
                                                data-testid={`brand-image-${index}`}
                                                id={`brand-image-${index}`}
                                            />
                                        </div>
                                    </Link>
                                    <h3 data-testid={`brand-label-${index}`} id={`brand-label-${index}`}>
                                        {capitalize(item.label)}
                                    </h3>
                                </div>
                            );
                        })
                        : sortedBrands?.slice(0, 12).map((item: SelectOptionsProps, index: Key) => {
                            return (
                                <div key={index} data-testid={`brand-item-${index}`} id={`brand-item-${index}`}>
                                    <Link
                                        href={`${currentUrl}/${getTypeVehicleText()}/${formatToSlug(item.value)}`}
                                        className="brand-logo"
                                        data-testid={`brand-link-${index}`}
                                        id={`brand-link-${index}`}
                                    >
                                        <div data-testid={`brand-image-container-${index}`} id={`brand-image-container-${index}`}>
                                            <Image
                                                src={`${process.env.NEXT_PUBLIC_API_BASE_URL}/imagens/prod/portal/img/logos/${getTypeVehicleText(!PLURAL)}/${item.value}.webp?w=70&h=70&q=70&t=true&z=3106`}
                                                alt={`LOGO ${item.label}`}
                                                title={item.label}
                                                width="70"
                                                height="70"
                                                loading='lazy'
                                                data-testid={`brand-image-${index}`}
                                                id={`brand-image-${index}`}
                                            />
                                        </div>
                                    </Link>
                                    <h3 data-testid={`brand-label-${index}`} id={`brand-label-${index}`}>
                                        {capitalize(item.label)}
                                    </h3>
                                </div>
                            );
                        })}
                </div>

                <button
                    onClick={toggleList}
                    onKeyDown={toggleList}
                    className="button"
                    data-testid="toggle-list-button"
                    id="toggle-list-button"
                >
                    {listFullVisible ? 'Ver menos' : 'Ver todas as marcas'}
                </button>
            </div>
        </>
    );
}
